import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SectionHeader from "@/components/site/SectionHeader";
import { INDUSTRIES } from "@/components/site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG = "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg";

const initial = {
  company_name: "", contact_person: "", phone: "", email: "", location: "", industry: "",
  designation: "", positions: "", salary: "", employment_type: "Full-time", joining_date: "", additional: "",
};

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function EmployersPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/employer-enquiries`, form);
      toast.success("Thank you.", { description: "Our recruitment team will contact you shortly." });
      setForm(initial);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="employers-page">
      <section className="section-pad bg-white">
        <div className="container-tight grid lg:grid-cols-[1fr,1.3fr] gap-14">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="overline mb-4">For Employers</p>
            <h1 className="font-serif text-4xl lg:text-5xl text-navy-800 leading-[1.05]">Looking for the Right Workforce?</h1>
            <p className="mt-5 text-muted-foreground leading-relaxed">Tell us your manpower requirement and our recruitment team will get in touch with you.</p>
            <div className="mt-8 rounded-3xl overflow-hidden border border-border">
              <img src={IMG} alt="Employer discussion" className="w-full h-[280px] object-cover" />
            </div>
          </div>

          <form onSubmit={submit} className="bg-secondary/40 rounded-3xl border border-border p-6 lg:p-10 grid gap-5 sm:grid-cols-2">
            <F label="Company Name*"><Input required value={form.company_name} onChange={on("company_name")} data-testid="emp-company" /></F>
            <F label="Contact Person*"><Input required value={form.contact_person} onChange={on("contact_person")} data-testid="emp-contact" /></F>
            <F label="Phone Number*"><Input required value={form.phone} onChange={on("phone")} data-testid="emp-phone" /></F>
            <F label="Email*"><Input required type="email" value={form.email} onChange={on("email")} data-testid="emp-email" /></F>
            <F label="Location*"><Input required value={form.location} onChange={on("location")} data-testid="emp-location" /></F>
            <F label="Industry*">
              <select required value={form.industry} onChange={on("industry")} data-testid="emp-industry" className="h-10 w-full bg-white border border-input rounded-md px-3 text-sm">
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i.name}>{i.name}</option>)}
              </select>
            </F>
            <F label="Designation Required*"><Input required value={form.designation} onChange={on("designation")} data-testid="emp-designation" /></F>
            <F label="Number of Positions*"><Input required value={form.positions} onChange={on("positions")} data-testid="emp-positions" placeholder="e.g. 10" /></F>
            <F label="Salary / CTC*"><Input required value={form.salary} onChange={on("salary")} data-testid="emp-salary" placeholder="e.g. ₹18k - ₹22k" /></F>
            <F label="Employment Type*">
              <select value={form.employment_type} onChange={on("employment_type")} data-testid="emp-employment-type" className="h-10 w-full bg-white border border-input rounded-md px-3 text-sm">
                {["Full-time", "Part-time", "Contract", "Temporary", "Internship"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </F>
            <F label="Expected Joining Date"><Input type="date" value={form.joining_date} onChange={on("joining_date")} data-testid="emp-joining-date" /></F>
            <div className="sm:col-span-2">
              <F label="Additional Requirement"><Textarea rows={4} value={form.additional} onChange={on("additional")} data-testid="emp-additional" /></F>
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
              <p className="text-xs text-muted-foreground">By submitting, you agree to be contacted about your requirement.</p>
              <Button data-testid="emp-submit-btn" type="submit" disabled={submitting} className="bg-navy-800 hover:bg-navy-700 text-white rounded-full h-12 px-8 btn-lift">
                {submitting ? "Submitting..." : "Submit Requirement"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
